import { lazy, Suspense } from "react";
import { Center, Loader } from "@mantine/core";
import { Navigate, Route, Routes } from "react-router-dom";

import Layout from "./components/Layout";
import RequireAuth from "./components/RequireAuth";

/**
 * Route table per specs/frontend.md. Player browsing is public — stats are the draw, and
 * requiring an account to look at them would be a pointless wall. Anything tied to a
 * user's own roster sits behind RequireAuth.
 *
 * Pages are lazy-loaded so each route is its own chunk. Layout and RequireAuth stay eager:
 * they render on every route, so deferring them would just add a waterfall.
 *
 * A visitor landing on /players no longer downloads the roster editor, the scoring rule
 * editor and the trends chart before anything appears.
 */
const Players = lazy(() => import("./pages/Players"));
const PlayerDetail = lazy(() => import("./pages/PlayerDetail"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Teams = lazy(() => import("./pages/Teams"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const ScoringProfiles = lazy(() => import("./pages/ScoringProfiles"));

function RouteFallback() {
  return (
    <Center h="60vh">
      <Loader aria-label="Loading page" />
    </Center>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Navigate to="/players" replace />} />

        <Route
          path="login"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="register"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Register />
            </Suspense>
          }
        />

        <Route
          path="players"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Players />
            </Suspense>
          }
        />
        <Route
          path="players/:gsisId"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PlayerDetail />
            </Suspense>
          }
        />

        <Route element={<RequireAuth />}>
          <Route
            path="teams"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Teams />
              </Suspense>
            }
          />
          <Route
            path="teams/:teamId"
            element={
              <Suspense fallback={<RouteFallback />}>
                <TeamDetail />
              </Suspense>
            }
          />
          <Route
            path="scoring"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ScoringProfiles />
              </Suspense>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/players" replace />} />
      </Route>
    </Routes>
  );
}
